// api.js


import axios from "axios";

// Hosted backend API base
const API_BASE = process.env.REACT_APP_API_URL || "https://productivitytool-365bd-688734725593.asia-southeast1.run.app/api";

// Axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper for safe API calls
const safeApi = async (promise) => {
  try {
    const res = await promise;
    return res.data;
  } catch (err) {
    console.error("API ERROR:", err.response?.data || err.message);
    throw err.response?.data || err;
  }
};

// =======================================================
//                        AUTH
// =======================================================
export const loginUser = async (email, password) => {
  return safeApi(api.post("/auth/login", { email, password }));
};

export const registerUser = async (name, email, password) => {
  return safeApi(api.post("/auth/register", { name, email, password }));
};

export const createOrganization = async (data) => {
  return safeApi(api.post("/auth/organization", data));
};

// =======================================================
//                     ADMIN PROFILE
// =======================================================
export const updateAdminProfile = async (form) => {
  // form should be a FormData instance containing all fields including 'profile_picture'
  return safeApi(
    api.put("/admin/update-profile", form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  );
};

// =======================================================
//                     ADMIN PASSWORD
// =======================================================
export const changeAdminPassword = async (currentPassword, newPassword) => {
  return safeApi(
    api.post("/admin/change-password", { currentPassword, newPassword })
  );
};

// =======================================================
//                     WORKSPACES
// =======================================================
export const fetchWorkspaces = async (owner_id) => {
  const data = await safeApi(api.get(`/workspaces/admin/${owner_id}`));
  return data.workspaces;
};

export const createWorkspace = async (name, owner_id) => {
  return safeApi(api.post("/workspaces/create", { name, owner_id }));
};

export const fetchWorkspaceReports = async (workspace_id) => {
  return safeApi(api.get(`/reports/workspace/${workspace_id}`));
};

export const fetchWorkspaceMembers = async (workspace_id) => {
  return safeApi(api.get(`/workspaces/${workspace_id}/members`));
};

// =======================================================
//                  TIME ENTRIES
// =======================================================
export const fetchUserTimeEntries = async (user_id) => {
  const data = await safeApi(api.get(`/time-entries/user/${user_id}`));
  return data.entries || [];
};

export const fetchLiveEntry = async (user_id) => {
  try {
    const res = await api.get(`/time-entries/live/${user_id}`);
    return res.data.live || null;
  } catch {
    return null;
  }
};

// =======================================================
//                       PROJECTS
// =======================================================
export const createProject = async (workspace_id, name, description) => {
  return safeApi(
    api.post("/projects/create", { workspace_id, name, description })
  );
};

export const fetchProjects = async (workspace_id) => {
  return safeApi(api.get(`/projects/${workspace_id}`));
};

// =======================================================
//                         TASKS
// =======================================================
export const createTask = async (
  project_id,
  description,
  assigned_to,
  status,
  priority
) => {
  return safeApi(
    api.post("/tasks/create", {
      project_id,
      description,
      assigned_to: assigned_to || null,
      status,
      priority,
      start_date: null,
      due_date: null,
    })
  );
};

export const fetchTasks = async (project_id) => {
  return safeApi(api.get(`/tasks/${project_id}`));
};

// =======================================================
//                       MEMBERS / INVITES
// =======================================================
export const inviteMember = async (workspace_id, name, email, role) => {
  return safeApi(api.post("/members/invite", { workspace_id, name, email, role }));
};

export const acceptInvite = async (token, name, password) => {
  return safeApi(
    api.post("/members/accept-invite", { token, name, password })
  );
};

export const updateMemberStatus = async (member_id, status) => {
  return safeApi(
    api.post("/members/status", { member_id, status })
  );
};


export const fetchWorkspaceMembersForTasks = async (workspace_id) => {
  const data = await safeApi(api.get(`/workspaces/${workspace_id}/members`));
  return Array.isArray(data) ? data : data.members || [];
};

export const fetchWorkspaceMembersForAdmin = async (workspace_id) => {
  return safeApi(api.get(`/workspaces/${workspace_id}/members`));
};



// =======================================================
// DASHBOARD / LIVE DATA
// =======================================================
export const fetchLiveMembersEntries = async () => {
  return safeApi(api.get(`/time-entries/live-members`));
};

export const fetchAdminDashboardSummary = async () =>
  safeApi(api.get("/time-entries/dashboard/summary/admin"));

export const fetchAdminTopApps = async () =>
  safeApi(api.get("/time-entries/dashboard/top-apps/admin"));

export const fetchAdminTeamActivity = async () =>
  safeApi(api.get(`/time-entries/team-activity/admin`));

export const fetchProductivityTrend = async (timeRange = 'monthly') =>
  safeApi(api.get(`/time-entries/dashboard/productivity-trend?timeRange=${timeRange}`));

export const fetchAdminLiveTeam = async () => {
  return safeApi(api.get("/time-entries/team-live/admin"));
};

// ========================================================
//   MEMBER STATISTICS
//=========================================================

// Fetch total worked hours for a member
export const fetchUserStats = async (user_id) => {
  return safeApi(api.get(`/member-stats/${user_id}`));
};

// Fetch weekly summary
export const fetchWeeklySummary = async (user_id) => {
  return safeApi(api.get(`/member-stats/weekly/${user_id}`));
};

// Fetch app usage
export const fetchAppUsage = async (user_id, range) => {
  return safeApi(api.get(`/member-stats/apps/${user_id}?range=${range}`));
};

export const fetchAppUsageTable = async (user_id) => {
  return safeApi(api.get(`/member-stats/apps/table/${user_id}`));
};

// =======================================================
//                     SCREENSHOTS
// =======================================================

// Auto-screenshot settings - UPDATED to include blur intensity
export const updateScreenshotInterval = async (workspace_id, screenshot_interval, screenshot_blur_enabled, screenshot_blur_intensity = 8,
  screenshot_quality = 60, idle_threshold = 300, screenshot_retention_days = 30) => {
  console.log(" API: Updating screenshot settings with blur intensity:", screenshot_blur_intensity, idle_threshold );
  
  return safeApi(
    api.post("/screenshots/interval", { 
      workspace_id, 
      screenshot_interval,
      screenshot_blur_enabled,
      screenshot_blur_intensity,
      screenshot_quality,
      idle_threshold,
      screenshot_retention_days

    })
  );
};

// UPDATED to include blur intensity in response
export const getScreenshotInterval = async (workspace_id) => {
  const data = await safeApi(api.get(`/screenshots/interval/${workspace_id}`));
  console.log("📥 API: Received screenshot settings:", {
    blur_enabled: data.screenshot_blur_enabled,
    blur_intensity: data.screenshot_blur_intensity,
    idle_threshold: data.idle_threshold
  });
  
  return {
    screenshot_interval: data.screenshot_interval,
    screenshot_blur_enabled: data.screenshot_blur_enabled || false,
    screenshot_blur_intensity: data.screenshot_blur_intensity || 8,
    screenshot_quality: data.screenshot_quality || 60,
    idle_threshold: data.idle_threshold || 300
  };
};

export const getScreenshotStatus = async (workspace_id) => {
  return safeApi(api.get(`/screenshots/status/${workspace_id}`));
};

export const testAutoScreenshot = async (workspace_id) => {
  return safeApi(api.post("/screenshots/test-auto", { workspace_id }));
};

// Manual screenshot request (for admins)
export const requestScreenshot = async (user_id) => {
  return safeApi(api.post("/screenshots/request", { user_id }));
};

// Fetch all screenshots of a workspace
export const fetchWorkspaceScreenshots = async (workspace_id) => {
  const data = await safeApi(api.get(`/screenshots/workspace/${workspace_id}`));
  return data.screenshots || [];
};

// Get organization storage usage
export const fetchOrganizationStorage = async (workspace_id) => {
  return safeApi(api.get(`/screenshots/storage/${workspace_id}`));
};

// Fetch all screenshots for an organization
export const fetchOrganizationScreenshots = async (organization_id) => {
  const data = await safeApi(api.get(`/screenshots/organization/${organization_id}`));
  return data.screenshots || [];
};

export const deleteScreenshots = async (screenshot_ids) => {
  return safeApi(
    api.delete("/screenshots/delete", {
      data: { screenshot_ids }
    })
  );
};

// =======================================================
//                     MEETINGS
// =======================================================

export const startMeeting = async (time_entry_id) => {
  return safeApi(
    api.post("/time-entries/start-meeting", { time_entry_id })
  );
};

export const endMeeting = async (time_entry_id) => {
  return safeApi(
    api.post("/time-entries/end-meeting", { time_entry_id })
  );
};


// =======================================================
//                       TIMELINE
// =======================================================

// Get timeline data for segmented chart
export const fetchTimelineData = async (hours = 12) => {
  return safeApi(api.get(`/timeline/data?hours=${hours}`));
};

export const fetchMemberActivitySummary = async (range = "monthly") =>
  safeApi(api.get(`/member-activity/summary?range=${range}`));

// =======================================================
//                       SETTINGS
// =======================================================

// UPDATED: fetchSettings - only get screenshot interval
export const fetchSettings = async (workspace_id) => {
  try {
    // Get screenshot interval (this endpoint exists)
    const screenshotSettings = await getScreenshotInterval(workspace_id);
    
    // Return only screenshot interval for now
    // Other settings will come from defaults
    return {
      screenshot_interval: screenshotSettings.screenshot_interval || 600,
      screenshot_blur_enabled: screenshotSettings.screenshot_blur_enabled || false,
      screenshot_blur_intensity: screenshotSettings.screenshot_blur_intensity || 8,
      screenshot_enabled: screenshotSettings.is_enabled !== false,
      idle_threshold: screenshotSettings.idle_threshold || 300,
      // Default values for other settings
      productivity_tracking: true,
      activity_monitoring: true,
      app_tracking: true,
      url_tracking: true,
      daily_report_email: true,
      weekly_summary_email: false,
      screenshot_quality: "medium",
      keystroke_logging: false,
      privacy_blur: false,
      work_hours_start: "09:00",
      work_hours_end: "17:00",
      tracking_days: ["mon", "tue", "wed", "thu", "fri"]
    };
  } catch (err) {
    console.error("Error fetching settings:", err);
    // Return defaults
    return {
      screenshot_interval: 600,
      screenshot_blur_enabled: false,
      screenshot_blur_intensity: 8,
      screenshot_enabled: true,
      productivity_tracking: true,
      activity_monitoring: true,
      app_tracking: true,
      url_tracking: true,
      idle_threshold: 300,
      daily_report_email: true,
      weekly_summary_email: false,
      screenshot_quality: "medium",
      keystroke_logging: false,
      privacy_blur: false,
      work_hours_start: "09:00",
      work_hours_end: "17:00",
      tracking_days: ["mon", "tue", "wed", "thu", "fri"]
    };
  }
};

// UPDATED: updateSettings - ONLY update screenshot interval
export const updateSettings = async (workspace_id, settings) => {
  try {
    console.log("🔄 Updating settings for workspace:", workspace_id);
    
    // Only update screenshot interval (this endpoint exists)
    if (settings.screenshot_interval !== undefined || settings.idle_threshold !== undefined) {
      console.log("📸 Updating screenshot interval to:", settings.screenshot_interval);
      const result = await updateScreenshotInterval(
        workspace_id, 
        settings.screenshot_interval,
        settings.screenshot_blur_enabled || false,
        settings.screenshot_blur_intensity || 8,
        settings.idle_threshold || 300
      );
      console.log("✅ Screenshot interval updated:", result);
      
      // Return success message
      return {
        success: true,
        message: `Auto-screenshot interval set to ${settings.screenshot_interval} seconds`,
        data: result
      };
    }
    
    // For now, skip other settings since endpoint doesn't exist
    console.log("⚠️ Other settings skipped - /settings endpoint not available");
    
    return { 
      success: true, 
      message: "Screenshot interval updated (other settings require backend implementation)" 
    };
    
  } catch (err) {
    console.error("❌ Error updating settings:", err);
    throw err;
  }
};

// STORAGE USED DATA

export const getOrganizationStorage = async (workspace_id) => {
  try {
    const res = await fetch(
      `/api/screenshots/storage/${workspace_id}`
    );
    return await res.json();
  } catch (err) {
    console.error("Storage fetch error:", err);
    return null;
  }
};

// =======================================================
//                      User App Usage.
// =======================================================

// Fetch per-member app usage
export const fetchMemberAppUsage = async (userId, range = "daily", startDate, endDate) => {
  if (!startDate || !endDate) throw new Error("startDate and endDate are required");

  const query = `range=${range}&startDate=${startDate}&endDate=${endDate}`;
  return safeApi(api.get(`/app-usage/member/${userId}?${query}`));
};


// =======================================================
//                      BILLING
// =======================================================

export const createCheckoutSession = async (plan) => {
  return safeApi(api.post("/create-checkout-session", { plan }));
};


// ==========================================================
//                     SHAREABLE TOKEN AND LINK
//===========================================================

export const createShareableStatsLink = async (user_id) => {
  return safeApi(
    api.get(`/member-stats/view-token/${user_id}`)
  );
};

export const fetchSharedStats = async (token) => {
  return safeApi(
    api.get(`/member-stats/public/${token}`)
  );
};


//  MEMBER ANALYTICS 

export const fetchComparisonStats = async (user_id) => {
  return safeApi(api.get(`/member-analytics/comparison/${user_id}`));
};

export const fetchTrendStats = async (user_id, range) => {
  return safeApi(
    api.get(`/member-analytics/trend/${user_id}?range=${range}`)
  );
};



export default api;